import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import ar from './ar';
import en from './en';

export const SUPPORTED_LOCALES = ['ar', 'en'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'dbcx.locale';

/**
 * Translation namespaces.
 *
 * Split by module rather than kept in one file so a screen only pays for the
 * strings it uses, and so two people working on different modules do not conflict
 * in the same translation file on every change.
 */
export const NAMESPACES = [
    'common',
    'auth',
    'nav',
    'admin',
    'ai',
    'cc',
    'crm',
    'demo',
    'docs',
    'guide',
    'integrations',
    'measurement',
    'reporting',
    'queue',
    'showcase',
    'tour',
    'validation',
] as const;

/**
 * Is a value one of our supported locales?
 *
 * Used at every boundary where a locale arrives from outside — the URL, storage,
 * the server payload — so an unexpected value degrades to the default rather than
 * putting i18next into a state with no translations at all.
 */
export function isLocale(value: unknown): value is Locale {
    return (
        typeof value === 'string' &&
        (SUPPORTED_LOCALES as readonly string[]).includes(value)
    );
}

export function directionFor(locale: Locale): 'rtl' | 'ltr' {
    return locale === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Initialise i18next.
 *
 * Resources are bundled rather than fetched. The kiosk and waiting-area display
 * run unattended on branch hardware and must come up correctly on a flaky
 * connection; a translation fetch that fails would leave those screens showing
 * raw translation keys to customers.
 */
export function initI18n(initialLocale: Locale) {
    if (i18n.isInitialized) {
        return i18n;
    }

    void i18n.use(initReactI18next).init({
        lng: initialLocale,
        // Arabic is the primary language of the Bank's users; English is the
        // fallback for any key not yet translated. This ordering is deliberate —
        // a missing Arabic string shows English, which is usable, rather than
        // showing the key.
        fallbackLng: 'en',
        supportedLngs: [...SUPPORTED_LOCALES],
        ns: [...NAMESPACES],
        defaultNS: 'common',
        resources: { ar, en },
        interpolation: {
            // React already escapes interpolated values; escaping again would
            // double-encode Arabic text containing quotes or ampersands.
            escapeValue: false,
        },
        returnNull: false,
        // In development, a missing key should be loud. In production it should
        // fall back quietly rather than showing a developer artefact to a customer.
        saveMissing: import.meta.env.DEV,
        missingKeyHandler: import.meta.env.DEV
            ? (lngs, ns, key) => {
                  console.warn(
                      `[i18n] missing key "${ns}:${key}" for ${lngs.join(', ')}`,
                  );
              }
            : undefined,
    });

    return i18n;
}

/**
 * Switch language with no page reload (§1.2).
 *
 * Updates i18next, the document's `lang` and `dir`, and the persisted preference
 * together. Doing all three in one place is what prevents the common failure
 * where the text changes language but the layout stays left-to-right.
 */
export async function changeLocale(locale: Locale): Promise<void> {
    await i18n.changeLanguage(locale);

    applyDocumentLocale(locale);

    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // Private browsing or a locked-down kiosk profile can refuse storage.
        // The language still changes for this session; only persistence is lost,
        // which is not worth failing the switch over.
    }

    // Mirrors the choice into a cookie so server-rendered surfaces — the Blade
    // shell on next load, and emailed/PDF output triggered from this session —
    // agree with the SPA.
    document.cookie = `locale=${locale};path=/;max-age=31536000;samesite=lax`;
}

export function applyDocumentLocale(locale: Locale): void {
    const root = document.documentElement;

    root.lang = locale;
    root.dir = directionFor(locale);
}

export default i18n;
